import { useCallback, useEffect, useRef, useState } from 'react';

/** Wait for SPA hydration after the iframe load event. */
const IFRAME_READY_SETTLE_MS = 600;
/** If load never fires (cache / cross-origin), still reveal the canvas. */
const IFRAME_READY_FALLBACK_MS = 12000;

const clearTimeoutRef = (ref) => {
  if (ref.current !== null) {
    window.clearTimeout(ref.current);
    ref.current = null;
  }
};

export const usePreviewIframeGate = ({
  previewUrl,
  iframeReloadKey,
  isSandboxLoading,
  isSessionExpired,
  isGateActive
}) => {
  const iframeRef = useRef(null);
  const loadSettleTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const isIframeReadyRef = useRef(false);

  const [isIframeReady, setIsIframeReady] = useState(false);
  const [awaitingIframeReady, setAwaitingIframeReady] = useState(false);

  const previewIframeKey = previewUrl ? `${previewUrl}::${iframeReloadKey}` : '';

  const markIframeReady = useCallback(() => {
    if (isIframeReadyRef.current) {
      return;
    }

    isIframeReadyRef.current = true;
    setIsIframeReady(true);
    setAwaitingIframeReady(false);
    clearTimeoutRef(loadSettleTimerRef);
    clearTimeoutRef(fallbackTimerRef);
  }, []);

  const scheduleIframeReady = useCallback(() => {
    clearTimeoutRef(loadSettleTimerRef);
    loadSettleTimerRef.current = window.setTimeout(markIframeReady, IFRAME_READY_SETTLE_MS);
  }, [markIframeReady]);

  const resetIframeGate = useCallback(() => {
    clearTimeoutRef(loadSettleTimerRef);
    clearTimeoutRef(fallbackTimerRef);
    isIframeReadyRef.current = false;
    setIsIframeReady(false);
  }, []);

  const handlePreviewIframeLoad = useCallback(() => {
    if (!isGateActive) {
      return;
    }

    scheduleIframeReady();
  }, [isGateActive, scheduleIframeReady]);

  useEffect(() => {
    isIframeReadyRef.current = isIframeReady;
  }, [isIframeReady]);

  useEffect(() => {
    if (isSandboxLoading) {
      setAwaitingIframeReady(true);
      resetIframeGate();
      return;
    }

    if (!previewUrl || isSessionExpired) {
      setAwaitingIframeReady(false);
      resetIframeGate();
      return;
    }

    if (!isIframeReady) {
      setAwaitingIframeReady(true);
    }
  }, [isIframeReady, isSandboxLoading, isSessionExpired, previewUrl, resetIframeGate]);

  useEffect(() => {
    if (!isGateActive || !previewUrl || isSessionExpired || isSandboxLoading) {
      clearTimeoutRef(fallbackTimerRef);
      return undefined;
    }

    if (!awaitingIframeReady || isIframeReady) {
      return undefined;
    }

    clearTimeoutRef(fallbackTimerRef);
    fallbackTimerRef.current = window.setTimeout(markIframeReady, IFRAME_READY_FALLBACK_MS);

    return () => {
      clearTimeoutRef(fallbackTimerRef);
    };
  }, [awaitingIframeReady, isGateActive, isIframeReady, isSandboxLoading, isSessionExpired, markIframeReady, previewUrl, previewIframeKey]);

  useEffect(() => {
    if (!previewUrl || isSessionExpired || !awaitingIframeReady || isIframeReady) {
      return undefined;
    }

    const iframe = iframeRef.current;

    if (!iframe) {
      return undefined;
    }

    const handleLoad = () => {
      handlePreviewIframeLoad();
    };

    iframe.addEventListener('load', handleLoad);

    try {
      if (iframe.contentDocument?.readyState === 'complete') {
        handlePreviewIframeLoad();
      }
    } catch {
      // Cross-origin sandbox — rely on load event or fallback timers.
    }

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [
    awaitingIframeReady,
    handlePreviewIframeLoad,
    isIframeReady,
    isSessionExpired,
    previewIframeKey,
    previewUrl
  ]);

  useEffect(
    () => () => {
      clearTimeoutRef(loadSettleTimerRef);
      clearTimeoutRef(fallbackTimerRef);
    },
    []
  );

  return {
    iframeRef,
    previewIframeKey,
    isIframeReady,
    awaitingIframeReady,
    handlePreviewIframeLoad,
    resetIframeGate
  };
};
